import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useSearchParams } from "react-router-dom";
import { type AssetFilters, useOwnsAnything } from "../api/assetlib";
import { useImports } from "../api/imports";
import { useMe } from "../api/me";
import { useDebouncedValue } from "../ui/useDebouncedValue";
import { Button, Card, Input, PageHeader, Select } from "../ui";
import { AssetGrid, type CategoryCount } from "./AssetGrid";
import {
  OBJECT_CATEGORIES,
  SURFACE_CATEGORIES,
  categoryWord,
} from "./categories";
import { IMPORT_STATE_WORDS } from "./ImportsPage";
import { SectionHeader } from "./SectionHeader";

type Kind = "object" | "material" | "skin" | "figure";
export type Tier = "all" | "curated" | "org";

const KINDS: { id: Kind; label: string }[] = [
  { id: "object", label: "Objects" },
  { id: "material", label: "Surfaces" },
  { id: "skin", label: "Artwork" },
  // P29: the figures a film's people are drawn as (actions are data, not
  // listed)
  { id: "figure", label: "People" },
];
export const TIERS: { id: Tier; label: string }[] = [
  { id: "all", label: "All" },
  { id: "curated", label: "TrueReel's" },
  { id: "org", label: "Yours" },
];

/** One choice among a few, as the composer's mode switch draws it. On a phone
 *  each choice is tall enough for a thumb. */
export function Choice<T extends string>({
  label,
  options,
  value,
  onChange,
}: {
  label: string;
  options: { id: T; label: string }[];
  value: T;
  onChange: (value: T) => void;
}) {
  return (
    <div
      role="radiogroup"
      aria-label={label}
      className="inline-flex gap-1 rounded-pill bg-slate-100 p-1"
    >
      {options.map((o) => {
        const checked = o.id === value;
        return (
          <button
            key={o.id}
            type="button"
            role="radio"
            aria-checked={checked}
            onClick={() => onChange(o.id)}
            className={[
              "min-h-[44px] rounded-pill px-3 py-1 text-sm font-semibold transition-colors sm:min-h-0",
              checked
                ? "bg-white text-brand-dark shadow-card"
                : "text-slate-600 hover:text-brand-dark",
            ].join(" ")}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

/** What the People tab holds, in one sentence. */
export const PEOPLE_INTRO =
  "Stylised figures that play the people in your films. Say who they are and what they do, and TrueReel picks one; their clothes can take your brand's colours.";

const STRIP_KEY = "assets.strip.dismissed";
/** An object every environment has, to try the steps on. */
const STRIP_OBJECT = "/assets/shape/phone-modern";
const STRIP_STEPS: { text: string; to?: string }[] = [
  { text: "Browse" },
  { text: "Dress it and save the look", to: STRIP_OBJECT },
  { text: "Use it in an ad", to: STRIP_OBJECT },
];

function stripDismissed(): boolean {
  try {
    return localStorage.getItem(STRIP_KEY) === "1";
  } catch {
    return false; // unreadable counts as not dismissed
  }
}

/** The three steps a first visitor sees: shown while the organisation has
 *  nothing of its own, until **Got it**. Not shown while that is still being
 *  asked, so it never flashes. */
function FirstVisitStrip() {
  const owns = useOwnsAnything();
  const [dismissed, setDismissed] = useState(stripDismissed);
  if (owns !== false || dismissed) return null;
  return (
    <Card
      className="mb-6 flex flex-col gap-3 p-4 sm:p-6"
      data-testid="first-visit"
    >
      <ol className="m-0 flex list-none flex-col gap-x-8 gap-y-1 p-0 text-sm font-semibold text-slate-900 sm:flex-row sm:flex-wrap">
        {STRIP_STEPS.map((step, i) => (
          <li
            key={step.text}
            className="flex min-h-[44px] items-center gap-2 sm:min-h-0"
          >
            <span
              aria-hidden="true"
              className="inline-flex h-6 w-6 flex-none items-center justify-center rounded-pill bg-brand-tint text-xs text-brand-dark"
            >
              {i + 1}
            </span>
            {step.to ? (
              <Link
                to={step.to}
                className="inline-flex min-h-[44px] items-center underline sm:min-h-0"
              >
                {step.text}
              </Link>
            ) : (
              <span>{step.text}</span>
            )}
          </li>
        ))}
      </ol>
      <p className="m-0 text-sm text-slate-600">
        Put your own label or screen on any object: open it, select its label or
        screen under Surfaces, and press Choose artwork.
      </p>
      <div>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="min-h-[44px] sm:min-h-0"
          onClick={() => {
            try {
              localStorage.setItem(STRIP_KEY, "1");
            } catch {
              /* private mode: gone for this visit */
            }
            setDismissed(true);
          }}
        >
          Got it
        </Button>
      </div>
    </Card>
  );
}

/** "Waiting for you": the organisation's models that are not in its 3D
 *  library yet (not uploaded, being checked, ready to review, or failed).
 *  Nothing is asked, and nothing is shown, while imports are switched off or
 *  there are none. */
function WaitingStrip() {
  const enabled = useMe().data?.features?.imports === true;
  const imports = useImports({ enabled });
  const items = enabled ? (imports.data?.items ?? []) : [];
  if (items.length === 0) return null;
  return (
    <Card className="mb-6 flex flex-col gap-3 p-4 sm:p-6" data-testid="waiting">
      <h2 className="tb-h-row m-0">Waiting for you</h2>
      <ul className="m-0 grid list-none grid-cols-1 gap-2 p-0 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item) => (
          <li key={item.id}>
            <Link
              to={`/assets/imports/${encodeURIComponent(item.id)}`}
              className="flex min-h-[44px] flex-col justify-center rounded-lg bg-slate-100 px-3 py-2 no-underline hover:bg-brand-tint"
              data-testid="waiting-card"
            >
              <span className="break-words text-sm font-semibold text-slate-800">
                {item.name}
              </span>
              <span className="text-xs text-slate-600">
                {IMPORT_STATE_WORDS[item.state] ?? ""}
              </span>
            </Link>
          </li>
        ))}
      </ul>
      <div>
        <Link
          to="/assets/imports"
          className="inline-flex min-h-[44px] items-center text-sm underline sm:min-h-0"
        >
          All imports
        </Link>
      </div>
    </Card>
  );
}

/** `/assets`: what the 3D library holds. The address is the state: the search,
 *  the two filters and the Favourites toggle live in the query string, with a
 *  default left out. */
export function AssetsPage() {
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const q = (params.get("q") ?? "").trim();
  const kindParam = params.get("kind");
  const kind: Kind =
    kindParam === "material" || kindParam === "skin" || kindParam === "figure"
      ? kindParam
      : "object";
  const tierParam = params.get("tier");
  const tier: Tier =
    tierParam === "curated" || tierParam === "org" ? tierParam : "all";

  const favorites = params.get("favorites") === "1";
  // A category of the kind shown; any other value is no filter.
  const kindCategories: readonly string[] =
    kind === "object"
      ? OBJECT_CATEGORIES
      : kind === "material"
        ? SURFACE_CATEGORIES
        : [];
  const categoryParam = params.get("category") ?? "";
  const category = kindCategories.includes(categoryParam) ? categoryParam : "";
  // What the list counted for each category of what the other filters keep.
  const [counts, setCounts] = useState<CategoryCount[]>([]);

  const change = (
    name: "q" | "kind" | "tier" | "favorites" | "category",
    value: string,
  ) => {
    const next = new URLSearchParams(params);
    if (value) next.set(name, value);
    else next.delete(name);
    // A category belongs to one kind: another kind shows them all.
    if (name === "kind") next.delete("category");
    setParams(next, { replace: true });
  };

  // The box shows what is typed at once; the address (and so the request)
  // follows 300 ms after the typing stops.
  const [typed, setTyped] = useState(q);
  const debounced = useDebouncedValue(typed, 300).trim();
  const written = useRef(q);
  const changeRef = useRef(change);
  changeRef.current = change;
  useEffect(() => {
    if (debounced === written.current) return;
    written.current = debounced;
    changeRef.current("q", debounced);
  }, [debounced]);
  // A search that arrives from outside (a link, Clear search) fills the box.
  useEffect(() => {
    if (q === written.current) return;
    written.current = q;
    setTyped(q);
  }, [q]);

  // Where the inspector's breadcrumb leads back to.
  useEffect(() => {
    try {
      sessionStorage.setItem("assets.back", location.search);
    } catch {
      /* private mode */
    }
  }, [location.search]);

  const filters = useMemo<AssetFilters>(
    () => ({
      kind,
      ...(q ? { q } : {}),
      ...(tier === "all" ? {} : { tier }),
      ...(favorites ? { favorites } : {}),
      ...(category ? { category } : {}),
    }),
    [kind, q, tier, favorites, category],
  );
  // The chosen category stays offered while the list is asked again.
  const offered =
    counts.some((c) => c.id === category) || !category
      ? counts
      : [...counts, { id: category, count: 0 }];
  // Deleted on an inspector page (it arrives in the navigation's state) or
  // from a card's menu on this page.
  const [deletedHere, setDeletedHere] = useState(false);
  const deleted =
    deletedHere ||
    (location.state as { deleted?: boolean } | null)?.deleted === true;

  return (
    <>
      <PageHeader eyebrow="— Objects" title="Objects" />
      <SectionHeader />
      {/* People say what they are for themselves: a figure is not asked for
          with Use in a new ad, it is picked by who the film shows. */}
      {kind === "figure" ? (
        <p
          className="mb-6 mt-0 text-sm text-slate-600"
          data-testid="people-intro"
        >
          {PEOPLE_INTRO}
        </p>
      ) : (
        <p className="mb-6 mt-0 text-sm text-slate-600">
          TrueReel picks from these when it writes your ad. To ask for one, open
          it and press Use in a new ad.
        </p>
      )}
      <FirstVisitStrip />
      <WaitingStrip />
      {deleted ? (
        <p role="status" className="mb-4 mt-0 text-sm text-slate-700">
          Deleted.
        </p>
      ) : null}
      <div className="mb-6 flex flex-col gap-3 lg:flex-row lg:items-center">
        <Input
          type="search"
          aria-label="Search Objects"
          placeholder="Search"
          className="min-h-[44px] sm:min-h-0 lg:max-w-xs"
          value={typed}
          onChange={(e) => setTyped(e.target.value)}
        />
        <div className="flex flex-wrap gap-3">
          <Choice
            label="Show"
            options={KINDS}
            value={kind}
            onChange={(value) =>
              change("kind", value === "object" ? "" : value)
            }
          />
          <Choice
            label="Whose"
            options={TIERS}
            value={tier}
            onChange={(value) => change("tier", value === "all" ? "" : value)}
          />
          {kindCategories.length > 0 ? (
            <label className="inline-flex items-center gap-2 text-sm font-semibold text-slate-700">
              <span>Category</span>
              <Select
                data-testid="category-filter"
                className="min-h-[44px] sm:min-h-0"
                value={category}
                onChange={(event) => change("category", event.target.value)}
              >
                <option value="">All categories</option>
                {offered.map((c) => (
                  <option key={c.id} value={c.id}>
                    {`${categoryWord(c.id)} (${c.count})`}
                  </option>
                ))}
              </Select>
            </label>
          ) : null}
          <div className="inline-flex rounded-pill bg-slate-100 p-1">
            <button
              type="button"
              aria-pressed={favorites}
              onClick={() => change("favorites", favorites ? "" : "1")}
              className={[
                "min-h-[44px] rounded-pill px-3 py-1 text-sm font-semibold transition-colors sm:min-h-0",
                favorites
                  ? "bg-white text-brand-dark shadow-card"
                  : "text-slate-600 hover:text-brand-dark",
              ].join(" ")}
            >
              Favourites
            </button>
          </div>
        </div>
      </div>
      <AssetGrid
        filters={filters}
        headings
        onCategories={setCounts}
        onDeleted={() => setDeletedHere(true)}
        onClearSearch={() => {
          written.current = "";
          setTyped("");
          change("q", "");
        }}
      />
    </>
  );
}
